import { jsx } from "react/jsx-runtime";
import { useRef, useState, useEffect, useCallback, Fragment } from "react";
import styled from "styled-components";
import { s4, s8, colors } from "@ardoq/design-tokens";
import { partition, last, zip, sum } from "lodash";
const RESIZER_CLASS_NAME = "resizer";
const HorizontalResizableSectionContainer = styled.div`
  flex: 1;
  height: 100%;
  box-sizing: border-box;
  ${({ $hasHorizontalScroll }) => $hasHorizontalScroll && "overflow-x: auto;"}
`;
const HorizontalContainer = styled.div`
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
`;
const HorizontalResizer = styled(styled.div`
  margin: 0 -${s4};
  width: ${s8};
  z-index: 2;
  height: 100%;
  cursor: ew-resize;
  pointer-events: all;
  background-color: ${(props) => props.$isActive ? colors.transparent10 : "transparent"};
  &:hover {
    background-color: ${colors.transparent10};
  }
`).attrs({ className: RESIZER_CLASS_NAME })``;
const VerticalResizableSectionContainer = styled.div`
  flex: 1;
  width: 100%;
  box-sizing: border-box;
`;
const VerticalContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;
const VerticalResizer = styled(styled.div`
  margin: -${s4} 0;
  height: ${s8};
  z-index: 1;
  width: 100%;
  cursor: ns-resize;
  pointer-events: all;
  background-color: ${(props) => props.$isActive ? colors.transparent10 : "transparent"};
  &:hover {
    background-color: ${colors.transparent10};
  }
`).attrs({ className: RESIZER_CLASS_NAME })``;
var Direction = /* @__PURE__ */ ((Direction2) => {
  Direction2[Direction2["HORIZONTAL"] = 1] = "HORIZONTAL";
  Direction2[Direction2["VERTICAL"] = 2] = "VERTICAL";
  return Direction2;
})(Direction || {});
const updateConfigs = (state, containerElement, getValueFromDOMRect) => {
  if (!containerElement) return state;
  const { boxes, containerBox } = getContainerState(containerElement);
  const configs = updateConfigsFromBoxes({
    configs: state.configs,
    boxes,
    containerBox,
    getValueFromDOMRect,
    isStyleInitialized: state.isStyleInitialized
  });
  return {
    ...state,
    startState: { ...state.startState, boxes, containerBox },
    configs
  };
};
const updateConfigsFromBoxes = ({
  configs,
  boxes,
  containerBox,
  getValueFromDOMRect,
  isStyleInitialized
}) => configs.map((config, index) => {
  const value = getCurrentValue({
    configs,
    boxes,
    containerBox,
    getValueFromDOMRect,
    isStyleInitialized,
    config,
    index
  });
  return {
    ...config,
    currentValue: value,
    startValue: value
  };
});
const getCurrentValue = ({
  configs,
  boxes,
  containerBox,
  getValueFromDOMRect,
  isStyleInitialized,
  config: { isCollapsed, shouldPreserveValue, currentValue, initialValue },
  index
}) => {
  if (!isCollapsed) {
    return getValueFromDOMRect(boxes[index]);
  }
  if (shouldPreserveValue) {
    if (isStyleInitialized) return currentValue;
    if (isNumber(initialValue)) return initialValue;
  }
  return getValueFromDOMRect(containerBox) / configs.length;
};
const isNumber = (value) => Number.isFinite(value);
const getContainerState = (containerElement) => {
  const [resizers, sections] = partition(
    Array.from(containerElement.children),
    (child) => child.classList.contains(RESIZER_CLASS_NAME)
  );
  const boxes = sections.map((child) => child.getBoundingClientRect());
  return {
    resizers,
    boxes,
    containerBox: containerElement.getBoundingClientRect()
  };
};
const consumeDeltaSubtract = (delta, configIndex, state) => {
  const config = state.configs[configIndex];
  if (!config) return [state, 0];
  const direction = delta >= 0 ? 1 : -1;
  if (config.isCollapsed) {
    const [newState2, propagatedDelta2] = consumeDeltaSubtract(
      delta,
      configIndex + direction,
      state
    );
    return [newState2, propagatedDelta2];
  }
  const maxConsumableDelta = direction * (config.startValue - config.minValue);
  if (direction * maxConsumableDelta >= direction * delta) {
    const newValue2 = config.startValue - direction * delta;
    return [updateStateWithNewValue(state, configIndex, newValue2), delta];
  }
  const newValue = config.startValue - direction * maxConsumableDelta;
  const [newState, propagatedDelta] = state.isPropagate ? consumeDeltaSubtract(
    delta - maxConsumableDelta,
    configIndex + direction,
    state
  ) : [state, direction * maxConsumableDelta];
  return [
    updateStateWithNewValue(newState, configIndex, newValue),
    maxConsumableDelta + propagatedDelta
  ];
};
const consumeDeltaAdd = (delta, configIndex, state) => {
  const config = state.configs[configIndex];
  if (!config) return [state, 0];
  const direction = delta >= 0 ? 1 : -1;
  if (config.isCollapsed) {
    const [newState2, propagatedDelta2] = consumeDeltaAdd(
      delta,
      configIndex + direction,
      state
    );
    return [newState2, propagatedDelta2];
  }
  const maxConsumableDelta = direction * (config.maxValue - config.startValue);
  if (direction * maxConsumableDelta >= direction * delta) {
    const newValue2 = config.startValue + direction * delta;
    return [updateStateWithNewValue(state, configIndex, newValue2), delta];
  }
  const newValue = config.startValue - direction * maxConsumableDelta;
  const [newState, propagatedDelta] = state.isPropagate ? consumeDeltaAdd(
    delta - maxConsumableDelta,
    configIndex + direction,
    state
  ) : [state, direction * maxConsumableDelta];
  return [
    updateStateWithNewValue(newState, configIndex, newValue),
    maxConsumableDelta + propagatedDelta
  ];
};
const updateStateWithNewValue = (state, configIndex, currentValue) => {
  const config = state.configs[configIndex];
  if (!config) return state;
  return {
    ...state,
    configs: state.configs.map(
      (config2, index) => index === configIndex ? { ...config2, currentValue } : config2
    )
  };
};
const isResizerDisabled = (acc, { isCollapsed }) => [...acc, (last(acc) ?? true) && isCollapsed];
const setStyles = (state) => {
  const prop = state.direction === Direction.HORIZONTAL ? "width" : "height";
  const minProp = state.direction === Direction.HORIZONTAL ? "minWidth" : "minHeight";
  const disabledResizersFromStart = state.configs.reduce(isResizerDisabled, []);
  const disabledResizersFromEnd = Array.from(state.configs).reverse().reduce(isResizerDisabled, []).reverse();
  const disabledResizers = zip(
    disabledResizersFromStart.slice(0, -1),
    disabledResizersFromEnd.slice(1)
  ).map(([a, b]) => a || b);
  return {
    ...state,
    styles: state.configs.map(
      ({ currentValue, minValue, isCollapsed, collapsedValue }) => isCollapsed ? {
        flex: "none",
        [prop]: collapsedValue
      } : {
        flex: "auto",
        [prop]: currentValue,
        [minProp]: minValue
      }
    ),
    resizerStyles: disabledResizers.map(
      (isDisabled) => isDisabled ? { display: "none" } : {}
    ),
    isStyleInitialized: true
  };
};
const getNewValue = ({ startState: { containerBox }, configs, direction }, configIndex) => {
  const value = direction === Direction.HORIZONTAL ? containerBox.width : containerBox.height;
  return value - sum(
    configs.filter((_, index) => index !== configIndex).map(
      ({ currentValue, isCollapsed, collapsedValue }) => isCollapsed ? collapsedValue : currentValue
    )
  );
};
const getHorizontalConfigFromChild = ({
  props: {
    isCollapsed = false,
    minWidth = 0,
    maxWidth = Infinity,
    collapsedWidth = 0,
    initialWidth,
    shouldPreserveValue = false
  }
}) => ({
  isCollapsed,
  minValue: minWidth,
  maxValue: maxWidth,
  initialValue: initialWidth,
  collapsedValue: collapsedWidth,
  startValue: 0,
  currentValue: 0,
  shouldPreserveValue
});
const getVerticalConfigFromChild = ({
  props: {
    isCollapsed = false,
    minHeight = 0,
    maxHeight = Infinity,
    collapsedHeight = 0,
    initialHeight,
    shouldPreserveValue = false
  }
}) => ({
  isCollapsed,
  minValue: minHeight,
  maxValue: maxHeight,
  initialValue: initialHeight,
  collapsedValue: collapsedHeight,
  startValue: 0,
  currentValue: 0,
  shouldPreserveValue
});
const haveChildrenDimensionsConfigPropsChangedHorizontal = (children, internalConfigsState) => children.map((child, index) => ({
  currentConfig: internalConfigsState[index],
  nextConfig: getHorizontalConfigFromChild(child)
})).some(
  ({ currentConfig, nextConfig }) => !isEqualDimensionConfig(currentConfig, nextConfig)
);
const haveChildrenDimensionsConfigPropsChangedVertical = (children, internalConfigsState) => children.map((child, index) => ({
  currentConfig: internalConfigsState[index],
  nextConfig: getVerticalConfigFromChild(child)
})).some(
  ({ currentConfig, nextConfig }) => !isEqualDimensionConfig(currentConfig, nextConfig)
);
const isEqualDimensionConfig = (currentConfig, nextConfig) => ["minValue", "maxValue", "initialValue"].every(
  (key) => currentConfig[key] === nextConfig[key]
);
const reduceMousePosition = (state, mousePoition) => {
  const delta = state.direction === Direction.HORIZONTAL ? mousePoition.x - state.startState.x : mousePoition.y - state.startState.y;
  return setStyles(processDelta(delta, state));
};
const processDelta = (delta, state) => {
  const { configs } = state;
  if (delta === 0) return state;
  let configIndex = state.activeResizerIndex + (delta > 0 ? 0 : 1);
  const nextConfigIndex = configIndex + (delta > 0 ? 1 : -1);
  while (configs[configIndex] && configs[configIndex].isCollapsed) {
    configIndex += delta > 0 ? -1 : 1;
  }
  if (configs[configIndex] && configs[nextConfigIndex]) {
    const [newState] = consumeDeltaSubtract(delta, nextConfigIndex, state);
    const newValue = getNewValue(newState, configIndex);
    return updateStateWithNewValue(newState, configIndex, newValue);
  }
  return state;
};
const getMouseDownHandler = ({
  containerRef,
  setState,
  getValueFromDOMRect,
  onResizeEnd,
  onResizeStart
}) => (event) => {
  const { target, clientX, clientY } = event;
  if (!((containerRef == null ? void 0 : containerRef.current) && target instanceof HTMLDivElement && target.classList.contains(RESIZER_CLASS_NAME))) {
    return;
  }
  event.preventDefault();
  event.stopPropagation();
  const { resizers, boxes, containerBox } = getContainerState(
    containerRef.current
  );
  const activeResizerIndex = resizers.indexOf(target);
  const startState = {
    x: clientX,
    y: clientY,
    boxes,
    containerBox
  };
  const mouseMoveHandler = ({ clientX: x, clientY: y }) => setState((state) => reduceMousePosition(state, { x, y }));
  const mouseUpHandler = () => {
    document.removeEventListener("mousemove", mouseMoveHandler);
    document.removeEventListener("mouseup", mouseUpHandler);
    setState((state) => ({ ...state, activeResizerIndex: -1 }));
    if (containerRef.current) {
      onResizeEnd == null ? void 0 : onResizeEnd(getContainerState(containerRef.current).boxes);
    }
  };
  document.addEventListener("mousemove", mouseMoveHandler);
  document.addEventListener("mouseup", mouseUpHandler);
  setState((state) => ({
    ...state,
    startState,
    configs: updateConfigsFromBoxes({
      configs: state.configs,
      boxes,
      containerBox,
      getValueFromDOMRect,
      isStyleInitialized: state.isStyleInitialized
    }),
    activeResizerIndex
  }));
  if (containerRef.current) {
    onResizeStart == null ? void 0 : onResizeStart(getContainerState(containerRef.current).boxes);
  }
};
const reduceIsCollapsed = (state, configIndex, isCollapsed, skipReducing = false, onResizeEnd) => {
  if (skipReducing) {
    return setStyles(state);
  }
  const config = state.configs[configIndex];
  if (!config) return state;
  const delta = config.currentValue - config.collapsedValue;
  const consumeDelta = isCollapsed ? consumeDeltaAdd : consumeDeltaSubtract;
  const [newStateLeft, consumedDeltaLeft] = consumeDelta(
    delta,
    configIndex + 1,
    state
  );
  if (Math.round(consumedDeltaLeft) === Math.round(delta)) {
    return setStyles({
      ...newStateLeft,
      isResizeEndScheduled: Boolean(onResizeEnd),
      configs: newStateLeft.configs.map(
        (config2, index) => index === configIndex ? { ...config2, isCollapsed } : config2
      )
    });
  }
  const [newStateRight] = consumeDelta(
    consumedDeltaLeft - delta,
    configIndex - 1,
    newStateLeft
  );
  return setStyles({
    ...newStateRight,
    isResizeEndScheduled: Boolean(onResizeEnd),
    configs: newStateRight.configs.map(
      (config2, index) => index === configIndex ? { ...config2, isCollapsed } : config2
    )
  });
};
const reduceIsCollapsedAllChildren = (state, {
  children,
  containerRef,
  onResizeEnd,
  getValueFromDOMRect
}) => {
  if (children.every(
    ({ props: { isCollapsed } }, index) => typeof isCollapsed !== "boolean" || state.isStyleInitialized && state.configs[index].isCollapsed === isCollapsed
  )) {
    return state;
  }
  return children.reduce(
    (state2, { props: { isCollapsed } }, index) => {
      if (typeof isCollapsed !== "boolean") return state2;
      const skipReducing = !state2.isStyleInitialized;
      return state2.isStyleInitialized && state2.configs[index].isCollapsed === isCollapsed ? state2 : reduceIsCollapsed(state2, index, isCollapsed, skipReducing);
    },
    {
      ...updateConfigs(state, containerRef.current, getValueFromDOMRect),
      isResizeEndScheduled: Boolean(onResizeEnd)
    }
  );
};
const ResizeableHorizontal = ({
  children,
  isPropagate = true,
  onResizeEnd,
  onResizeStart,
  hasHorizontalScroll = false
}) => {
  const containerRef = useRef(null);
  const [
    {
      activeResizerIndex,
      styles,
      configs,
      resizerStyles,
      isResizeEndScheduled
    },
    setState
  ] = useState({
    startState: { x: 0, y: 0, boxes: [], containerBox: new DOMRect() },
    configs: children.map(getHorizontalConfigFromChild),
    activeResizerIndex: -1,
    styles: [],
    resizerStyles: [],
    isPropagate,
    direction: Direction.HORIZONTAL,
    isStyleInitialized: false,
    isResizeEndScheduled: false
  });
  useEffect(() => {
    if (haveChildrenDimensionsConfigPropsChangedHorizontal(children, configs)) {
      setState((previousState) => ({
        ...previousState,
        startState: { x: 0, y: 0, boxes: [], containerBox: new DOMRect() },
        configs: children.map(getHorizontalConfigFromChild),
        activeResizerIndex: -1,
        styles: [],
        resizerStyles: []
      }));
    }
  }, [children, configs]);
  useEffect(() => {
    if (containerRef.current && // Check if the component is rendered and has a visible size.
    // This is crucial because if an ancestor has 'display: none',
    // getClientRects() would return an empty list, leading to incorrect
    // calculations.
    containerRef.current.getClientRects().length > 0) {
      setState(
        (state) => reduceIsCollapsedAllChildren(state, {
          children,
          containerRef,
          onResizeEnd,
          getValueFromDOMRect: (rect) => rect.width
        })
      );
    }
  }, [children, containerRef, onResizeEnd, setState]);
  useEffect(() => {
    if (isResizeEndScheduled) {
      if (onResizeEnd && containerRef.current) {
        onResizeEnd(getContainerState(containerRef.current).boxes);
      }
      setState((state) => ({ ...state, isResizeEndScheduled: false }));
    }
  }, [containerRef, isResizeEndScheduled, onResizeEnd, setState]);
  const mouseDownHandler = useCallback(
    getMouseDownHandler({
      containerRef,
      setState,
      getValueFromDOMRect: (rect) => rect.width,
      onResizeEnd,
      onResizeStart
    }),
    [containerRef, setState]
  );
  const getSetCollapsed = useCallback(
    (index) => (isCollapsed) => setState(
      (state) => reduceIsCollapsed(
        updateConfigs(
          state,
          containerRef.current,
          (rect) => rect.width
        ),
        index,
        isCollapsed,
        false,
        onResizeEnd
      )
    ),
    [setState, onResizeEnd]
  );
  const isLast = (index) => index === children.length - 1;
  return /* @__PURE__ */ jsx(HorizontalContainer, { onMouseDown: mouseDownHandler, ref: containerRef, children: children.flatMap(({ type: Section, props }, index) => [
    /* @__PURE__ */ jsx(
      HorizontalResizableSectionContainer,
      {
        style: styles[index] ?? { maxWidth: configs[index].initialValue },
        $hasHorizontalScroll: hasHorizontalScroll,
        children: /* @__PURE__ */ jsx(
          Section,
          {
            ...props,
            isCollapsed: configs[index].isCollapsed,
            setCollapsed: getSetCollapsed(index)
          }
        )
      },
      `section-${index}`
    ),
    !isLast(index) && /* @__PURE__ */ jsx(
      HorizontalResizer,
      {
        $isActive: index === activeResizerIndex,
        style: resizerStyles[index]
      },
      `resizer-${index}`
    )
  ]) });
};
const ResizeableVertical = ({
  children,
  isPropagate = true,
  onResizeEnd,
  onResizeStart
}) => {
  const containerRef = useRef(null);
  const [
    {
      activeResizerIndex,
      styles,
      configs,
      resizerStyles,
      isResizeEndScheduled
    },
    setState
  ] = useState({
    startState: { x: 0, y: 0, boxes: [], containerBox: new DOMRect() },
    configs: children.map(getVerticalConfigFromChild),
    activeResizerIndex: -1,
    styles: [],
    resizerStyles: [],
    isPropagate,
    direction: Direction.VERTICAL,
    isStyleInitialized: false,
    isResizeEndScheduled: false
  });
  useEffect(() => {
    if (haveChildrenDimensionsConfigPropsChangedVertical(children, configs)) {
      setState((previousState) => ({
        ...previousState,
        startState: { x: 0, y: 0, boxes: [], containerBox: new DOMRect() },
        configs: children.map(getVerticalConfigFromChild),
        activeResizerIndex: -1,
        styles: [],
        resizerStyles: []
      }));
    }
  }, [children, configs]);
  useEffect(() => {
    if (containerRef.current) {
      setState(
        (state) => reduceIsCollapsedAllChildren(state, {
          children,
          containerRef,
          onResizeEnd,
          getValueFromDOMRect: (rect) => rect.height
        })
      );
    }
  }, [children, containerRef, onResizeEnd, setState]);
  useEffect(() => {
    if (isResizeEndScheduled) {
      if (onResizeEnd && containerRef.current) {
        onResizeEnd(getContainerState(containerRef.current).boxes);
      }
      setState((state) => ({ ...state, isResizeEndScheduled: false }));
    }
  }, [containerRef, isResizeEndScheduled, onResizeEnd, setState]);
  const mouseDownHandler = useCallback(
    getMouseDownHandler({
      containerRef,
      setState,
      getValueFromDOMRect: (rect) => rect.height,
      onResizeEnd,
      onResizeStart
    }),
    [containerRef, setState]
  );
  const getSetCollapsed = useCallback(
    (index) => (isCollapsed) => setState(
      (state) => reduceIsCollapsed(
        updateConfigs(
          state,
          containerRef.current,
          (rect) => rect.height
        ),
        index,
        isCollapsed
      )
    ),
    [setState]
  );
  const isLast = (index) => index === children.length - 1;
  return /* @__PURE__ */ jsx(VerticalContainer, { onMouseDown: mouseDownHandler, ref: containerRef, children: children.flatMap(({ type: Section, props }, index) => [
    /* @__PURE__ */ jsx(
      VerticalResizableSectionContainer,
      {
        style: styles[index] ?? { maxHeight: configs[index].initialValue },
        children: /* @__PURE__ */ jsx(
          Section,
          {
            ...props,
            isCollapsed: configs[index].isCollapsed,
            setCollapsed: getSetCollapsed(index)
          }
        )
      },
      `section-${index}`
    ),
    !isLast(index) && /* @__PURE__ */ jsx(
      VerticalResizer,
      {
        $isActive: index === activeResizerIndex,
        style: resizerStyles[index]
      },
      `resizer-${index}`
    )
  ]) });
};
const HorizontalResizeableContainerWrapper = ({
  children
}) => {
  return /* @__PURE__ */ jsx(Fragment, { children });
};
export {
  HorizontalResizeableContainerWrapper,
  ResizeableHorizontal as ResizableHorizontal,
  ResizeableVertical as ResizableVertical
};
//# sourceMappingURL=index.js.map
